import React, { useMemo, useState } from "react";
import api from "../api/axios";
import "./Register.responsive.css";

export default function Register({ onSwitch }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
  });

  const [loading, setLoading] = useState(false);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const passwordStrength = useMemo(() => {
    const password = form.password || "";

    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    if (!password) {
      return {
        label: "Password not entered",
        className: "empty",
        width: "0%",
      };
    }

    if (score <= 1) {
      return {
        label: "Weak password",
        className: "weak",
        width: "28%",
      };
    }

    if (score === 2 || score === 3) {
      return {
        label: "Good password",
        className: "good",
        width: "68%",
      };
    }

    return {
      label: "Strong password",
      className: "strong",
      width: "100%",
    };
  }, [form.password]);

  const validateForm = () => {
    if (!form.name.trim()) {
      alert("Please enter your full name");
      return false;
    }

    if (!form.email.trim()) {
      alert("Please enter your email");
      return false;
    }

    if (!form.password.trim()) {
      alert("Please create a password");
      return false;
    }

    if (form.password.length < 8) {
      alert("Password must be at least 8 characters long");
      return false;
    }

    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role === "patient" ? "user" : form.role,
      };

      await api.post("/api/auth/register", payload);

      alert("Registered successfully. Please login to continue.");
      onSwitch();
    } catch (err) {
      const detail = err?.response?.data?.detail;

      if (Array.isArray(detail)) {
        alert(detail.map((item) => item.msg).join("\n"));
      } else {
        alert(detail || "Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleRegister();
  };

  return (
    <div className="medgen-register-screen">
      <style>{registerCss}</style>

      <div className="medgen-register-bg-orb orb-one" />
      <div className="medgen-register-bg-orb orb-two" />

      <section className="medgen-register-shell">
        <aside className="medgen-register-intel">
          <div className="medgen-intel-top">
            <div className="medgen-logo-mark">
              <img src="/assets/favicon.png" alt="MedGen icon" />
            </div>

            <div>
              <span>MedGen Smart System</span>
              <h1>Medical Intelligence</h1>
            </div>
          </div>

          <p className="medgen-intel-copy">
            Set up secure access for patient intake, clinical review, smart case
            sheets, family-linked profiles, brain impact review, and research
            workflow support.
          </p>

          <div className="medgen-onboard-flow">
            <div className="medgen-flow-item active">
              <b>01</b>
              <div>
                <strong>Create secure account</strong>
                <p>Register a verified workspace identity for clinical access.</p>
              </div>
            </div>

            <div className="medgen-flow-item">
              <b>02</b>
              <div>
                <strong>Add patient profiles</strong>
                <p>Build patient and family-linked health records.</p>
              </div>
            </div>

            <div className="medgen-flow-item">
              <b>03</b>
              <div>
                <strong>Start clinical sessions</strong>
                <p>Continue intake, notes, analysis, and structured review.</p>
              </div>
            </div>

            <div className="medgen-flow-item">
              <b>04</b>
              <div>
                <strong>Review smart insights</strong>
                <p>Use dashboard, brain map, and research tools together.</p>
              </div>
            </div>
          </div>

          <div className="medgen-trust-panel">
            <div>
              <span>Workspace posture</span>
              <strong>Secure by design</strong>
            </div>

            <div className="medgen-trust-grid">
              <i>Patient intake</i>
              <i>Case sheets</i>
              <i>Brain map</i>
              <i>Research graph</i>
            </div>
          </div>
        </aside>

        <main className="medgen-register-card-wrap">
          <div className="medgen-register-card">
            <div className="medgen-form-logo">
              <img src="/assets/logo.png" alt="MedGen" />
            </div>

            <div className="medgen-form-heading">
              <div className="medgen-eyebrow">Create account</div>
              <h2>Register for MedGen</h2>
              <p>
                Create your smart medical assistant account and start building a
                secure clinical workspace.
              </p>
            </div>

            <form onSubmit={onSubmit} className="medgen-register-form">
              <div className="medgen-form-field">
                <label htmlFor="register-name">Full name</label>
                <div className="medgen-input-shell">
                  <span>◎</span>
                  <input
                    id="register-name"
                    type="text"
                    placeholder="Enter full name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    autoComplete="name"
                  />
                </div>
              </div>

              <div className="medgen-form-field">
                <label htmlFor="register-email">Email address</label>
                <div className="medgen-input-shell">
                  <span>@</span>
                  <input
                    id="register-email"
                    type="email"
                    placeholder="doctor@clinic.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="medgen-form-field">
                <label htmlFor="register-password">Password</label>
                <div className="medgen-input-shell">
                  <span>●</span>
                  <input
                    id="register-password"
                    type="password"
                    placeholder="Minimum 8 characters"
                    value={form.password}
                    onChange={(e) => update("password", e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="medgen-password-meter">
                  <div className="medgen-password-track">
                    <i
                      className={passwordStrength.className}
                      style={{ width: passwordStrength.width }}
                    />
                  </div>
                  <small>{passwordStrength.label}</small>
                </div>
              </div>

              <div className="medgen-form-field">
                <label htmlFor="register-role">Workspace role</label>
                <div className="medgen-select-shell">
                  <span>▣</span>
                  <select
                    id="register-role"
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                  >
                    <option value="user">User</option>
                  </select>
                </div>
              </div>

              <button
                className="medgen-register-btn"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="medgen-spinner" />
                    Creating account...
                  </>
                ) : (
                  <>Create Workspace Account</>
                )}
              </button>

              <div className="medgen-auth-footer">
                <span>Already registered?</span>
                <button type="button" onClick={onSwitch}>
                  ← Back to login
                </button>
              </div>
            </form>

            <div className="medgen-secure-note">
              <span>Account security</span>
              <b>
                User registration creates a protected workspace identity for
                future patient and clinical workflows.
              </b>
            </div>
          </div>
        </main>
      </section>
    </div>
  );
}

const registerCss = `
.medgen-register-screen {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  place-items: center;
  padding: 34px;
  color: #07111f;
  background:
    radial-gradient(circle at 12% 4%, rgba(24, 183, 255, 0.18), transparent 28%),
    radial-gradient(circle at 92% 82%, rgba(31, 123, 193, 0.16), transparent 30%),
    linear-gradient(135deg, #f6fbff 0%, #edf6ff 45%, #ffffff 100%);
}

.medgen-register-screen * {
  box-sizing: border-box;
}

.medgen-register-bg-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(4px);
  pointer-events: none;
}

.medgen-register-bg-orb.orb-one {
  width: 420px;
  height: 420px;
  left: -160px;
  top: -160px;
  background: rgba(24, 183, 255, 0.14);
}

.medgen-register-bg-orb.orb-two {
  width: 360px;
  height: 360px;
  right: -140px;
  bottom: -160px;
  background: rgba(31, 123, 193, 0.13);
}

.medgen-register-shell {
  width: min(1180px, 100%);
  min-height: 720px;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(420px, 1.04fr) minmax(390px, 0.96fr);
  border-radius: 34px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(185, 210, 235, 0.82);
  box-shadow: 0 34px 90px rgba(7, 17, 31, 0.14);
  backdrop-filter: blur(18px);
}

.medgen-register-intel {
  position: relative;
  isolation: isolate;
  padding: 42px;
  color: #ffffff;
  background:
    radial-gradient(circle at 82% 8%, rgba(24, 183, 255, 0.24), transparent 28%),
    radial-gradient(circle at 10% 90%, rgba(31, 123, 193, 0.22), transparent 34%),
    linear-gradient(145deg, #03060c 0%, #06101f 48%, #0b1b30 100%);
}

.medgen-register-intel::before {
  content: "";
  position: absolute;
  inset: 22px;
  z-index: -1;
  border-radius: 26px;
  border: 1px solid rgba(24, 183, 255, 0.12);
  pointer-events: none;
}

.medgen-intel-top {
  display: flex;
  align-items: center;
  gap: 16px;
}

.medgen-logo-mark {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  display: grid;
  place-items: center;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(24, 183, 255, 0.18);
  box-shadow: 0 18px 42px rgba(24, 183, 255, 0.16);
}

.medgen-logo-mark img {
  width: 46px;
  height: 46px;
  object-fit: contain;
}

.medgen-intel-top span {
  display: block;
  margin-bottom: 5px;
  color: #9fdcff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.medgen-intel-top h1 {
  margin: 0;
  max-width: 460px;
  color: #ffffff;
  font-size: clamp(32px, 4vw, 48px);
  line-height: 1.02;
  letter-spacing: -0.055em;
  font-weight: 950;
}

.medgen-intel-copy {
  max-width: 560px;
  margin: 28px 0 28px;
  color: #a9bdd3;
  font-size: 15px;
  line-height: 1.75;
}

.medgen-onboard-flow {
  display: grid;
  gap: 12px;
}

.medgen-flow-item {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 14px;
  align-items: start;
  padding: 15px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(255, 255, 255, 0.10);
}

.medgen-flow-item.active {
  background: rgba(255, 255, 255, 0.96);
  color: #07111f;
}

.medgen-flow-item b {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: #65cfff;
  background: rgba(24, 183, 255, 0.10);
  font-size: 11px;
  font-weight: 950;
}

.medgen-flow-item.active b {
  color: #125a94;
  background: rgba(31, 123, 193, 0.12);
}

.medgen-flow-item strong {
  display: block;
  margin-bottom: 4px;
  color: inherit;
  font-size: 15px;
  font-weight: 900;
}

.medgen-flow-item p {
  margin: 0;
  color: inherit;
  opacity: 0.72;
  font-size: 13px;
  line-height: 1.45;
}

.medgen-trust-panel {
  margin-top: 24px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24, 183, 255, 0.13);
}

.medgen-trust-panel span {
  display: block;
  margin-bottom: 5px;
  color: #9fdcff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.medgen-trust-panel strong {
  display: block;
  color: #ffffff;
  font-size: 18px;
  font-weight: 950;
}

.medgen-trust-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.medgen-trust-grid i {
  min-height: 38px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: #bfeaff;
  background: rgba(24, 183, 255, 0.09);
  border: 1px solid rgba(24, 183, 255, 0.13);
  font-size: 12px;
  font-weight: 850;
  font-style: normal;
}

.medgen-register-card-wrap {
  display: grid;
  place-items: center;
  padding: 42px;
}

.medgen-register-card {
  width: min(430px, 100%);
  padding: 34px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(185, 210, 235, 0.92);
  box-shadow: 0 24px 65px rgba(7, 17, 31, 0.10);
}

/* Dark capsule so the white Med text in logo.png is visible */
.medgen-form-logo {
  width: 100%;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 18px 22px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 18% 0%, rgba(24, 183, 255, 0.22), transparent 34%),
    linear-gradient(135deg, #03060c 0%, #06101f 52%, #0b1b30 100%);
  border: 1px solid rgba(24, 183, 255, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.035),
    0 18px 40px rgba(3, 6, 12, 0.16);
}

.medgen-form-logo img {
  display: block;
  width: auto;
  height: auto;
  max-width: 260px;
  max-height: 58px;
  object-fit: contain;
  object-position: center;
  filter: drop-shadow(0 0 12px rgba(24, 183, 255, 0.18));
}

.medgen-form-heading {
  text-align: center;
  margin-bottom: 24px;
}

.medgen-eyebrow {
  margin-bottom: 8px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.medgen-form-heading h2 {
  margin: 0;
  color: #07111f;
  font-size: 32px;
  line-height: 1.08;
  letter-spacing: -0.055em;
  font-weight: 950;
}

.medgen-form-heading p {
  margin: 12px auto 0;
  max-width: 330px;
  color: #66778e;
  font-size: 14px;
  line-height: 1.65;
}

.medgen-register-form {
  display: grid;
  gap: 15px;
}

.medgen-form-field {
  display: grid;
  gap: 8px;
}

.medgen-form-field label {
  color: #30445d;
  font-size: 12px;
  font-weight: 900;
}

.medgen-input-shell,
.medgen-select-shell {
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: center;
  min-height: 50px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid #cddcec;
  overflow: hidden;
  transition: 0.16s ease;
}

.medgen-input-shell:focus-within,
.medgen-select-shell:focus-within {
  background: #ffffff;
  border-color: #1f7bc1;
  box-shadow: 0 0 0 4px rgba(31, 123, 193, 0.12);
}

.medgen-input-shell span,
.medgen-select-shell span {
  display: grid;
  place-items: center;
  color: #1f7bc1;
  font-size: 13px;
  font-weight: 950;
}

.medgen-input-shell input,
.medgen-select-shell select {
  width: 100%;
  height: 100%;
  min-height: 50px;
  border: none;
  outline: none;
  background: transparent;
  color: #07111f;
  font-size: 14px;
  font-weight: 700;
}

.medgen-select-shell select {
  cursor: pointer;
  appearance: none;
}

.medgen-input-shell input::placeholder {
  color: #8aa0b8;
  font-weight: 600;
}

.medgen-password-meter {
  display: grid;
  gap: 6px;
}

.medgen-password-track {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8f0f8;
  border: 1px solid #dbe8f4;
}

.medgen-password-track i {
  height: 100%;
  display: block;
  border-radius: inherit;
  transition: width 0.18s ease;
}

.medgen-password-track i.empty {
  width: 0;
}

.medgen-password-track i.weak {
  background: linear-gradient(90deg, #ef4444, #f97316);
}

.medgen-password-track i.good {
  background: linear-gradient(90deg, #f59e0b, #1f7bc1);
}

.medgen-password-track i.strong {
  background: linear-gradient(90deg, #1f7bc1, #18b7ff);
}

.medgen-password-meter small {
  color: #66778e;
  font-size: 11px;
  font-weight: 800;
}

.medgen-register-btn {
  width: 100%;
  min-height: 52px;
  margin-top: 4px;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  color: #ffffff;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 16px 34px rgba(31, 123, 193, 0.24);
  font-size: 14px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: 0.16s ease;
}

.medgen-register-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 20px 38px rgba(31, 123, 193, 0.28);
}

.medgen-register-btn:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.medgen-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.38);
  border-top-color: #ffffff;
  animation: medgenSpin 0.8s linear infinite;
}

@keyframes medgenSpin {
  to {
    transform: rotate(360deg);
  }
}

.medgen-auth-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  color: #66778e;
  font-size: 13px;
}

.medgen-auth-footer button {
  border: none;
  background: transparent;
  color: #125a94;
  cursor: pointer;
  font-weight: 950;
}

.medgen-secure-note {
  margin-top: 22px;
  padding: 14px;
  border-radius: 16px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.12), transparent 30%),
    #f6faff;
  border: 1px solid #d8e7f5;
}

.medgen-secure-note span {
  display: block;
  margin-bottom: 5px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.medgen-secure-note b {
  color: #30445d;
  font-size: 12px;
  line-height: 1.45;
}

@media (max-width: 980px) {
  .medgen-register-screen {
    padding: 18px;
  }

  .medgen-register-shell {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .medgen-register-intel {
    padding: 30px;
  }

  .medgen-register-card-wrap {
    padding: 26px;
  }

  .medgen-intel-top h1 {
    font-size: 34px;
  }
}

@media (max-width: 560px) {
  .medgen-register-screen {
    padding: 12px;
  }

  .medgen-register-shell {
    border-radius: 24px;
  }

  .medgen-register-intel {
    padding: 22px;
  }

  .medgen-register-card-wrap {
    padding: 18px;
  }

  .medgen-register-card {
    padding: 24px;
    border-radius: 22px;
  }

  .medgen-intel-top {
    align-items: flex-start;
  }

  .medgen-logo-mark {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
  }

  .medgen-logo-mark img {
    width: 38px;
    height: 38px;
  }

  .medgen-intel-top h1 {
    font-size: 28px;
  }

  .medgen-form-logo {
    min-height: 76px;
    padding: 14px 18px;
  }

  .medgen-form-logo img {
    max-width: 210px;
    max-height: 50px;
  }

  .medgen-form-heading h2 {
    font-size: 28px;
  }

  .medgen-trust-grid {
    grid-template-columns: 1fr;
  }

  .medgen-auth-footer {
    flex-direction: column;
    gap: 4px;
  }
}
`;